import {
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import type { OperatingCountry } from '@vrplatform/data';
import { useMemo } from 'react';

type Props = {
  countries: OperatingCountry[];
  countryCode: string | null;
  countryError?: string;
  description?: string;
  dropdownZIndex?: number;
  loading: boolean;
  onCountryChange: (country: OperatingCountry | null) => void;
  queryError?: string;
};

export const OperatingCountryFields = ({
  countries,
  countryCode,
  countryError,
  description = 'Your operating country determines where your team’s data is stored.',
  dropdownZIndex,
  loading,
  onCountryChange,
  queryError,
}: Props) => {
  const countryOptions = useMemo(
    () =>
      countries.map(
        (country) =>
          ({
            label: country.name,
            value: country.code,
          }) satisfies SelectItem
      ),
    [countries]
  );
  const unavailableError =
    queryError ||
    (!loading && countries.length === 0
      ? 'No operating countries are available.'
      : undefined);

  return (
    <InputWrapper
      label="Operating country"
      description={description}
      required
      error={unavailableError || countryError}
      styles={{
        label: {
          fontWeight: 500,
        },
      }}
    >
      <InputSelect
        value={
          countryOptions.find((option) => option.value === countryCode) ?? null
        }
        type="single"
        setValue={(nextValue) =>
          onCountryChange(
            countries.find((country) => country.code === nextValue?.value) ??
              null
          )
        }
        data={{ options: countryOptions }}
        dropdownProps={{
          withinPortal: true,
          width: 'target',
          zIndex: dropdownZIndex,
        }}
        inputProps={{
          disabled: !!unavailableError,
          error: !!(unavailableError || countryError),
          loadingQuery: loading,
          placeholder: 'Select an operating country',
        }}
      />
    </InputWrapper>
  );
};
